import Phaser from 'phaser';
import { GAME_WIDTH } from '../config';

export interface ActiveBuff {
  id: string;
  color: number;
  remainingMs: number;
  totalMs: number;
  description: string;
}

const ICON_SIZE = 22;
const GAP = 6;
const TIMER_FONT_SIZE = '8px';

interface BuffSlot {
  hitArea: Phaser.GameObjects.Rectangle;
  timerText: Phaser.GameObjects.Text;
  buffId: string | null;
  lastTimeStr: string;
  lastElapsed: number;
}

export class BuffBar {
  private scene: Phaser.Scene;
  private anchorRight: number;
  private y: number;
  private bg: Phaser.GameObjects.Graphics;
  private iconGfx: Phaser.GameObjects.Graphics;
  private overlayGfx: Phaser.GameObjects.Graphics;
  private slots: BuffSlot[] = [];

  // Tooltip
  private tooltipBg: Phaser.GameObjects.Graphics | null = null;
  private tooltipText: Phaser.GameObjects.Text | null = null;
  private hoveredBuffId: string | null = null;
  private currentBuffs: ActiveBuff[] = [];

  constructor(scene: Phaser.Scene, anchorRight: number, y: number) {
    this.scene = scene;
    this.anchorRight = anchorRight;
    this.y = y;

    this.bg = scene.add.graphics().setDepth(100);
    this.iconGfx = scene.add.graphics().setDepth(101);
    this.overlayGfx = scene.add.graphics().setDepth(102);
  }

  update(buffs: ActiveBuff[]): void {
    this.bg.clear();
    this.iconGfx.clear();
    this.overlayGfx.clear();
    this.currentBuffs = buffs;

    // Hide all existing slots
    for (const slot of this.slots) {
      slot.timerText.setVisible(false);
      slot.hitArea.setVisible(false);
      if (slot.hitArea.input) slot.hitArea.input.enabled = false;
      slot.buffId = null;
    }

    // If hovered buff is no longer active, hide tooltip
    if (this.hoveredBuffId && !buffs.find(b => b.id === this.hoveredBuffId)) {
      this.hideTooltip();
    }

    if (buffs.length === 0) return;

    const totalW = buffs.length * ICON_SIZE + Math.max(0, buffs.length - 1) * GAP;
    let x = this.anchorRight - totalW;

    for (let i = 0; i < buffs.length; i++) {
      const buff = buffs[i];

      // Background
      this.bg.fillStyle(0x1a1a2e, 0.9);
      this.bg.fillRoundedRect(x, this.y, ICON_SIZE, ICON_SIZE, 4);
      this.bg.lineStyle(1.5, buff.color, 0.8);
      this.bg.strokeRoundedRect(x, this.y, ICON_SIZE, ICON_SIZE, 4);

      // Draw icon
      this.drawBuffIcon(this.iconGfx, x, this.y, buff);

      // Ensure slot exists
      const slot = this.getOrCreateSlot(i);
      slot.buffId = buff.id;

      // Cooldown overlay (fills from top down as time elapses)
      const elapsed = 1 - (buff.remainingMs / buff.totalMs);

      // Throttle overlay drawing to avoid microscopic sub-pixel thrashing
      if (Math.abs(slot.lastElapsed - elapsed) > 0.02) {
        slot.lastElapsed = elapsed;
      }

      if (slot.lastElapsed > 0 && slot.lastElapsed < 1) {
        this.overlayGfx.fillStyle(0x000000, 0.5);
        const h = ICON_SIZE * slot.lastElapsed;
        this.overlayGfx.fillRoundedRect(x, this.y, ICON_SIZE, h, { tl: 4, tr: 4, bl: 0, br: 0 });
      }

      // Timer text caching
      const secs = Math.ceil(buff.remainingMs / 1000);
      const newTimeStr = `${secs}s`;

      if (slot.lastTimeStr !== newTimeStr) {
        slot.timerText.setText(newTimeStr);
        slot.lastTimeStr = newTimeStr;
      }

      slot.timerText.setPosition(x + ICON_SIZE / 2, this.y + ICON_SIZE + 2);
      slot.timerText.setVisible(true);

      // Hit area for hover
      slot.hitArea.setPosition(x + ICON_SIZE / 2, this.y + ICON_SIZE / 2);
      slot.hitArea.setVisible(true);
      if (slot.hitArea.input) slot.hitArea.input.enabled = true;

      // Update tooltip if this buff is hovered
      if (this.hoveredBuffId === buff.id) {
        this.showTooltip(x + ICON_SIZE / 2, this.y, buff);
      }

      x += ICON_SIZE + GAP;
    }
  }

  private getOrCreateSlot(index: number): BuffSlot {
    if (this.slots[index]) return this.slots[index];

    const timerText = this.scene.add.text(0, 0, '', {
      fontSize: TIMER_FONT_SIZE,
      fontFamily: 'Arial, sans-serif',
      color: '#cccccc',
      align: 'center',
    }).setOrigin(0.5, 0).setDepth(103);

    const hitArea = this.scene.add.rectangle(0, 0, ICON_SIZE, ICON_SIZE)
      .setInteractive({ useHandCursor: false })
      .setAlpha(0.001)
      .setDepth(104);

    hitArea.on('pointerover', () => {
      const slot = this.slots[index];
      if (slot.buffId) {
        this.hoveredBuffId = slot.buffId;
      }
    });

    hitArea.on('pointerout', () => {
      this.hoveredBuffId = null;
      this.hideTooltip();
    });

    const slot: BuffSlot = { hitArea, timerText, buffId: null, lastTimeStr: '', lastElapsed: -1 };
    this.slots[index] = slot;
    return slot;
  }

  private showTooltip(cx: number, y: number, buff: ActiveBuff): void {
    this.hideTooltip();

    const secs = Math.ceil(buff.remainingMs / 1000);
    const text = `${buff.description}\n${secs}s remaining`;

    const lines = text.split('\n');
    const lineHeight = 14;
    const padding = 6;
    const width = 160;
    const height = lines.length * lineHeight + padding * 2;

    let tooltipX = cx - width / 2;
    tooltipX = Math.max(10, Math.min(GAME_WIDTH - width - 10, tooltipX));
    const tooltipY = y + ICON_SIZE + 16;

    this.tooltipBg = this.scene.add.graphics();
    this.tooltipBg.fillStyle(0x1a1a2e, 0.95);
    this.tooltipBg.fillRoundedRect(tooltipX, tooltipY, width, height, 6);
    this.tooltipBg.lineStyle(1, buff.color, 0.5);
    this.tooltipBg.strokeRoundedRect(tooltipX, tooltipY, width, height, 6);
    this.tooltipBg.setDepth(1000);

    this.tooltipText = this.scene.add.text(tooltipX + padding, tooltipY + padding, text, {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      lineSpacing: 2,
    }).setDepth(1001);
  }

  private hideTooltip(): void {
    if (this.tooltipBg) { this.tooltipBg.destroy(); this.tooltipBg = null; }
    if (this.tooltipText) { this.tooltipText.destroy(); this.tooltipText = null; }
  }

  private drawBuffIcon(gfx: Phaser.GameObjects.Graphics, x: number, y: number, buff: ActiveBuff): void {
    const cx = x + ICON_SIZE / 2;
    const cy = y + ICON_SIZE / 2;

    switch (buff.id) {
      case 'gold_rush':
        // Gold coin
        gfx.fillStyle(buff.color, 1);
        gfx.fillCircle(cx, cy, 6);
        gfx.lineStyle(1, 0xaa8800, 0.8);
        gfx.strokeCircle(cx, cy, 4);
        break;

      case 'poison_arrows':
        // Poison drop
        gfx.fillStyle(buff.color, 1);
        gfx.fillTriangle(cx, cy - 6, cx - 4, cy + 2, cx + 4, cy + 2);
        gfx.fillCircle(cx, cy + 2, 4);
        break;

      default:
        // Generic colored circle
        gfx.fillStyle(buff.color, 0.8);
        gfx.fillCircle(cx, cy, 6);
        break;
    }
  }

  destroy(): void {
    this.hideTooltip();
    this.bg.destroy();
    this.iconGfx.destroy();
    this.overlayGfx.destroy();
    for (const slot of this.slots) {
      slot.timerText.destroy();
      slot.hitArea.destroy();
    }
    this.slots = [];
  }
}
